import { parseFractionDerivative, parseDerivative } from "./parseDerivative";
import { foldInlineChildren, foldInlineNode } from "./parseInline";
import { parseIntegral } from "./parseIntegral";
import { parseLimit } from "./parseLimit";
import { parseLogarithm } from "./parseLogarithm";
import { parseSumProduct } from "./parseSumProduct";
import { semanticToSympyCode } from "./semanticToSympyCode";
import { allUnitsAreSi, nodeJsonPlainText, op } from "./shared";
import { traverseNode } from "./traverseNode";
import { traverseRootNode } from "./traverseRoot";

import type { DenominatorPair } from "./parseDerivative";
import type { SemanticExpr } from "../types";
import type { NodeJson, NodeJsonMap } from "@corca/data";

/**
 * Holds the state for a single nodeToSemantic pipeline run and threads `this` through the free
 * parse functions. Replaces the former module-level mutable state (the two Sets in shared.ts and
 * the inlineLookup in semanticToSympyCode.ts). Delegating methods forward to the free functions,
 * passing `this` as the first argument so those functions read state off the builder. The free
 * functions import SympyBuilder TYPE-ONLY, so there is no runtime circular import.
 *
 * - boundVarsScope: names of bound variables (e.g. summation/integration index) currently in scope
 * - indexedBaseNames: variable display names (e.g. "a") that should be emitted as sp.IndexedBase
 *   because they appear with a subscript that matches a bound variable in scope.
 * - warnings: input the run could translate but not cleanly; nodeToSemantic returns them so the
 *   host can log them. Anything that stops the build throws SympyBuildError instead.
 */
export class SympyBuilder {
  variables: Map<string, string>;
  symbolNodes = new Map<string, NodeJson>();
  emitSiUnits: boolean;
  boundVarsScope = new Set<string>();
  indexedBaseNames = new Set<string>();
  warnings: string[] = [];

  constructor(
    node: NodeJson,
    existingVariables?: Map<string, string>,
    emitSiUnitsOverride?: boolean | null,
  ) {
    this.variables = existingVariables ?? new Map<string, string>();
    this.emitSiUnits = emitSiUnitsOverride ?? allUnitsAreSi(node);
  }

  /* Delegating methods — each forwards to the free function in its module, passing `this`. */

  build(node: NodeJson): SemanticExpr | null {
    return traverseRootNode(this, node);
  }

  traverseRootNode(node: NodeJson): SemanticExpr | null {
    return traverseRootNode(this, node);
  }

  traverseNode(node: NodeJson, level: number): SemanticExpr | null {
    return traverseNode(this, node, level);
  }

  foldInlineNode(node: NodeJson, level: number): SemanticExpr | null {
    return foldInlineNode(this, node, level);
  }

  foldInlineChildren(children: NodeJson[], level: number): SemanticExpr | null {
    return foldInlineChildren(this, children, level);
  }

  parseLimit(node: NodeJsonMap["function"]): SemanticExpr {
    return parseLimit(this, node);
  }

  parseIntegral(node: NodeJsonMap["function"]): SemanticExpr {
    return parseIntegral(this, node);
  }

  parseSumProduct(node: NodeJsonMap["function"], sympyFunc: "Sum" | "Product"): SemanticExpr {
    return parseSumProduct(this, node, sympyFunc);
  }

  parseDerivative(node: NodeJsonMap["operator"], order: number): SemanticExpr {
    return parseDerivative(this, node, order);
  }

  parseFractionDerivative(
    numeratorFunc: NodeJson,
    denominatorPairs: DenominatorPair[],
  ): SemanticExpr | null {
    return parseFractionDerivative(this, numeratorFunc, denominatorPairs);
  }

  parseLogarithm(node: NodeJsonMap["function"], level: number, defaultBase?: number): SemanticExpr {
    return parseLogarithm(this, node, level, defaultBase);
  }

  /* Stateful mini-helpers implemented directly here (small; no separate file). */

  warn(message: string): void {
    if (!this.warnings.includes(message)) this.warnings.push(message);
  }

  /**
   * If the variable node has a single subscript label (no superscript, no decorators) and that
   * subscript is a bound variable currently in scope, build `varBase[varSubscript]`
   * (operator "Indexed") and register the base name in indexedBaseNames so buildSympyCode emits
   * `sp.IndexedBase`. Returns null if the pattern does not apply.
   */
  tryEmitIndexedAccess(node: NodeJson): SemanticExpr | null {
    if (node.type !== "variable") return null;
    if (node.children[0].type !== "symbol") return null;
    const symbolNode = node.children[0];
    const baseDisplayName = symbolNode.props.value;
    if (typeof baseDisplayName !== "string" || baseDisplayName.length === 0) return null;

    let bottomLabel: NodeJson | null = null;
    for (const child of symbolNode.children) {
      if (child.type === "decorator") return null;
      if (child.type === "label") {
        if (child.props.position === "right-top") return null;
        if (child.props.position === "right-bottom") {
          if (bottomLabel) return null;
          bottomLabel = child;
        }
      }
    }
    if (!bottomLabel) return null;

    const subscriptText = bottomLabel.children.map(nodeJsonPlainText).join("");
    if (!this.boundVarsScope.has(subscriptText)) return null;

    if (!this.variables.has(baseDisplayName)) {
      this.variables.set(baseDisplayName, `var${this.variables.size}`);
    }
    this.indexedBaseNames.add(baseDisplayName);

    if (!this.symbolNodes.has(baseDisplayName)) {
      this.symbolNodes.set(baseDisplayName, {
        type: "variable",
        props: node.props,
        children: [{ type: "symbol", props: { value: baseDisplayName }, children: [] }],
      });
    }

    if (!this.variables.has(subscriptText)) {
      this.variables.set(subscriptText, `var${this.variables.size}`);
    }

    if (!this.symbolNodes.has(subscriptText)) {
      this.symbolNodes.set(subscriptText, {
        type: "variable",
        props: {},
        children: [{ type: "symbol", props: { value: subscriptText }, children: [] }],
      });
    }

    return op("Indexed", [
      { type: "symbol", name: baseDisplayName },
      { type: "symbol", name: subscriptText },
    ]);
  }

  /**
   * Render a SemanticExpr fragment to Python, resolving symbols through this run's variable map.
   * Used where the original code built a string and then did string surgery (limit `→` split,
   * sum `=` detection, derivative free_symbols templating, etc.).
   */
  emitInline(expr: SemanticExpr): string {
    return semanticToSympyCode(expr, this.variables);
  }
}
